"use client";

import { c as _c } from "react/compiler-runtime";
import classNames from "classnames";
import { useEffect, useReducer, useRef } from "react";
import LocationResultList from "src/components/locationresultlist/LocationResultList";
import type { ISearchAction } from "src/components/search/ISearchAction";
import Search from "src/components/search/Search";
import SearchContentList from "src/components/search/SearchContentList";
import TopOfPageSponsor from "src/components/takoversponsor/TopOfPageSponsor";
import { useAzLocationSearch, useAzSearch } from "src/lib/client/useSearch";
import { FULLSEARCH_DEFAULTS } from "src/lib/search/searchOverrides";
import styles from "./CompleteSearchResult.module.css";
import { type ISponsorProps } from "src/lib/types/ISponsorProps";
import { useRouter, useSearchParams } from "next/navigation";
import { locale } from "@locale";
export type CompleteSearchResultSponsorProps = Pick<ISponsorProps, "hasSponsor" | "sponsorClickUrl" | "sponsorTopImageDesktop" | "sponsorTopImageMobile">;

//#region [Props]
type CompleteSearchResultProps = {
  isStreamingOnly?: boolean;
  azShowScore?: boolean;
  customSortNum?: number | string;
  maxNumItems?: number;
  pathname: string;
} & CompleteSearchResultSponsorProps;
//#endregion

//#region [Component]
export default function CompleteSearchResult(t0) {
  const $ = _c(47);
  const {
    isStreamingOnly: t1,
    azShowScore: t2,
    customSortNum,
    hasSponsor,
    sponsorClickUrl,
    sponsorTopImageDesktop,
    sponsorTopImageMobile,
    maxNumItems: t3,
    pathname
  } = t0;
  const isStreamingOnly = t1 === undefined ? false : t1;
  const azShowScore = t2 === undefined ? false : t2;
  const maxNumItems = t3 === undefined ? 100 : t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      searchExpression: "",
      showSearchResults: false
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const [state, dispatch] = useReducer(searchReducer, t4 as ISearchState);
  const router = useRouter();
  const query = useSearchParams();
  let t5;
  if ($[1] !== query) {
    t5 = query?.get("q");
    $[1] = query;
    $[2] = t5;
  } else {
    t5 = $[2];
  }
  const q = t5;
  const _timeout = useRef(null);
  let t6;
  let t7;
  if ($[3] !== q) {
    t6 = () => {
      if (q) {
        if (q && q.trim() !== "") {
          dispatch({
            type: "DO_SEARCH",
            searchExpression: q
          });
        }
      }
    };
    t7 = [q];
    $[3] = q;
    $[4] = t6;
    $[5] = t7;
  } else {
    t6 = $[4];
    t7 = $[5];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[6] !== pathname || $[7] !== router || $[8] !== state.searchExpression) {
    t8 = () => {
      if (_timeout.current) {
        clearTimeout(_timeout.current);
        _timeout.current = null;
      }
      if (state.searchExpression && typeof window !== "undefined") {
        _timeout.current = window.setTimeout(() => {
          router.replace(`${pathname}?q=${encodeURIComponent(state.searchExpression)}`, {
            scroll: false
          });
          _timeout.current = null;
        }, 400);
      }
    };
    t9 = [state.searchExpression, pathname, router];
    $[6] = pathname;
    $[7] = router;
    $[8] = state.searchExpression;
    $[9] = t8;
    $[10] = t9;
  } else {
    t8 = $[9];
    t9 = $[10];
  }
  useEffect(t8, t9);
  const t10 = !!state.searchExpression && !isStreamingOnly;
  let t11;
  if ($[11] !== t10) {
    t11 = {
      active: t10
    };
    $[11] = t10;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  const {
    searchLoading: locationSearchLoading,
    searchResult: azLocationResult
  } = useAzLocationSearch(state.searchExpression, t11);
  const t12 = !!state.searchExpression;
  let t13;
  if ($[13] !== customSortNum || $[14] !== isStreamingOnly || $[15] !== maxNumItems || $[16] !== t12) {
    t13 = {
      streamingOnly: isStreamingOnly,
      maxNumItems,
      active: t12,
      customSortNum,
      defaults: FULLSEARCH_DEFAULTS
    };
    $[13] = customSortNum;
    $[14] = isStreamingOnly;
    $[15] = maxNumItems;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  const {
    searchLoading: azSearchLoading,
    searchResult: azSearchResult
  } = useAzSearch(state.searchExpression, t13);
  const searchLoading = azSearchLoading;
  const showLocationResult = !isStreamingOnly && !locationSearchLoading && (azLocationResult?.results?.length ?? 0) > 0;
  const t14 = (sponsorTopImageDesktop?.length ?? 0) > 0;
  let t15;
  if ($[18] !== t14) {
    t15 = classNames(styles.SSearch, {
      [styles.withSponsor]: t14
    });
    $[18] = t14;
    $[19] = t15;
  } else {
    t15 = $[19];
  }
  let t16;
  if ($[20] !== searchLoading || $[21] !== state.searchExpression || $[22] !== state.showSearchResults || $[23] !== t15) {
    t16 = <Search className={t15} dispatch={dispatch} searchExpression={state.searchExpression} showSearchResults={state.showSearchResults} placeholderText={locale.search.placeholder} isSearching={searchLoading} controlled={true} disableEnter={true} />;
    $[20] = searchLoading;
    $[21] = state.searchExpression;
    $[22] = state.showSearchResults;
    $[23] = t15;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== hasSponsor || $[26] !== sponsorClickUrl || $[27] !== sponsorTopImageDesktop || $[28] !== sponsorTopImageMobile) {
    t17 = hasSponsor && <TopOfPageSponsor className={styles.STopOfPageSponsor} desktopImages={sponsorTopImageDesktop} mobileImages={sponsorTopImageMobile} clickUrl={sponsorClickUrl} />;
    $[25] = hasSponsor;
    $[26] = sponsorClickUrl;
    $[27] = sponsorTopImageDesktop;
    $[28] = sponsorTopImageMobile;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== t16 || $[31] !== t17) {
    t18 = <div className={styles.SSearchInputContainer}>{t16}{t17}</div>;
    $[30] = t16;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== azLocationResult?.results || $[34] !== showLocationResult || $[35] !== state.searchExpression) {
    t19 = showLocationResult && <LocationResultList className={styles.SLocationResultList} locationList={azLocationResult?.results} maxNumToShow={100} clickTracker={locName => trackHit(false, "Kinosted", locName, state.searchExpression)} />;
    $[33] = azLocationResult?.results;
    $[34] = showLocationResult;
    $[35] = state.searchExpression;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== azSearchResult?.results || $[38] !== azShowScore || $[39] !== isStreamingOnly || $[40] !== searchLoading || $[41] !== state.searchExpression) {
    t20 = state.searchExpression?.trim() && <SearchContentList isSearching={searchLoading} contentList={azSearchResult?.results} itemClickTracker={(id, title) => trackHit(isStreamingOnly, title, id.toString(), state.searchExpression)} forceStreaming={isStreamingOnly} azShowScore={azShowScore} />;
    $[37] = azSearchResult?.results;
    $[38] = azShowScore;
    $[39] = isStreamingOnly;
    $[40] = searchLoading;
    $[41] = state.searchExpression;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== t18 || $[44] !== t19 || $[45] !== t20) {
    t21 = <div>{t18}{t19}{t20}</div>;
    $[43] = t18;
    $[44] = t19;
    $[45] = t20;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  return t21;
}
//#endregion

//#region [Other]
interface ISearchState {
  showSearchResults: boolean;
  searchExpression: string;
}
function searchReducer(state: ISearchState, action: ISearchAction): ISearchState {
  switch (action.type) {
    /*
    case 'BEGIN_SEARCH':
         return { ...state, showSearchResults: true, hasFiredFirstFocus: true };
        */
    case 'END_SEARCH':
      return {
        ...state,
        showSearchResults: false
      };
    case 'CLEAR_SEARCH':
      return {
        ...state,
        showSearchResults: false,
        searchExpression: ""
      };
    case 'DO_SEARCH':
      return {
        ...state,
        searchExpression: action.searchExpression ?? "",
        showSearchResults: true
      };
    default:
      return state;
  }
}
function trackHit(streamingOnly: boolean, title: string, id: string, searchExpression: string) {
  window?.dataLayer?.push({
    'event': `fw_Search${streamingOnly ? '_sg' : ''}`,
    'eventCategory': `${streamingOnly ? 'SG ' : ''}Søk Treff`,
    'eventAction': `${title} | ${id}`,
    'eventLabel': searchExpression
  });
}
//#endregion